import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import frDict from '@/i18n/fr.json';

type Language = 'fr' | 'en';
type Dict = Record<string, string>;

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

// FR (default language) is bundled with the initial chunk so the first paint
// is never blank. EN is split out and loaded on demand.
const cache: Partial<Record<Language, Dict>> = { fr: frDict as Dict };

function loadDict(lang: Language): Promise<Dict> {
  if (cache[lang]) return Promise.resolve(cache[lang] as Dict);
  return import('@/i18n/en.json').then((m) => {
    const dict = (m.default ?? m) as Dict;
    cache[lang] = dict;
    return dict;
  });
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const langParam = params.get('lang');
      if (langParam === 'en' || langParam === 'fr') return langParam;
    }
    return 'fr';
  });

  // Trigger re-render once the async dictionary finishes loading.
  const [, setVersion] = useState(0);

  useEffect(() => {
    document.documentElement.lang = language;
    if (!cache[language]) {
      loadDict(language).then(() => setVersion((v) => v + 1));
    }
  }, [language]);

  const setLanguage = useCallback((lang: Language) => {
    if (!cache[lang]) {
      loadDict(lang).then(() => setLanguageState(lang));
    } else {
      setLanguageState(lang);
    }
  }, []);

  const t = useCallback(
    (key: string): string => {
      const dict = cache[language] ?? cache.fr;
      return (dict && dict[key]) || key;
    },
    [language],
  );

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
